import actions from "~/shared/actions";
import type { Action } from "~/utils/actions";
import type { Period } from "~/utils/policies";

export interface AccountTransactionsState {
	allTransactions: Transaction[];
	errors: Record<number, Partial<Record<Period, TransactionError>>>;
	transactionDetails: TransactionDetails[];
	period: Period;
}

type TransactionError =
	| "SCHEDULED_DOWNTIME"
	| "AGREEMENT_DOES_NOT_EXIST"
	| "TRIGGER_ALREADY_ORDERED"
	| "MEDLEM_UTMELDT"
	| "FREMREGN_VENTEDE_TRANSAKSJONER"
	| "TOO_MANY_TRANSACTIONS"
	| "PRODUCT_ID_MISMATCH"
	| "GENERAL_ERROR"
	| "INVALID_EMAIL"
	| "NEW_POLICY_OFFER_EXISTS"
	| "FOR_MANGE_POSTER";

export interface Transaction {
	id: string;
	amount: number;
	date: number;
	description: string;
	status: string;
	period: Period[];
	policyNumber: number;
}

interface TransactionDetails extends Transaction {
	registrationDate: number;
	fundTransactions: FundTransaction[];
	policyNumber: number;
}

interface FundTransaction {
	fundName: string;
	tradeDate: number;
	units: number;
	amount: number;
	price: number;
	proportion: number;
}

export const defaultState: AccountTransactionsState = {
	allTransactions: [],
	errors: {},
	transactionDetails: [],
	period: "ONE_MONTH",
};

const merge = (existing, incoming, policyNumber, period) => {
	const newTransactions = incoming.map((transaction) => {
		const old = existing.find((t) => t.id === transaction.id && t.policyNumber === policyNumber);
		if (old) {
			return {
				...transaction,
				policyNumber,
				period: Array.from(new Set(old.period).add(period)),
			};
		}
		return {
			...transaction,
			policyNumber,
			period: [period],
		};
	});

	return existing.filter((t1) => !newTransactions.some((t2) => t1.id === t2.id && t1.policyNumber === t2.policyNumber)).concat(newTransactions);
};

const add = (existing, newItem) => {
	if (existing.find((t) => t.id === newItem.id)) {
		return existing;
	}
	return existing.concat(newItem);
};

export default function accountTransactionsReducer(state = defaultState, { data, type }: Action): AccountTransactionsState {
	const { accountTransactions, errorCode, period, policyNumber } = data || {};

	switch (type) {
		case actions.PERIOD_CHANGED: {
			return { ...state, period };
		}

		case actions.ACCOUNT_TRANSACTIONS_RESPONSE: {
			if (!errorCode) {
				return {
					...state,
					allTransactions: merge(state.allTransactions, accountTransactions, policyNumber, period),
				};
			}
			return {
				...state,
				errors: {
					...state.errors,
					[policyNumber]: {
						...state.errors[policyNumber],
						[period]: errorCode,
					},
				},
			};
		}
		case actions.ACCOUNT_TRANSACTION_RESPONSE: {
			return {
				...state,
				transactionDetails: add(state.transactionDetails, { ...data.transaction, policyNumber }),
			};
		}
		default:
			return state;
	}
}
